import classNames from 'classnames';
import * as React from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import type { BaseImage, PictureProfile } from '~/Partials/Picture/Picture';
import { Picture } from '~/Partials/Picture/Picture';
import { PictureProfiles } from '~/Partials/Picture/PictureProfiles';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import type { IconName } from '~/Partials/SvgIcon/SvgIcon';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { useClickProxyAllowingTextSelection } from '~/Utils/Hooks/useClickProxyAllowingTextSelection';
import type { Theme } from '~/Utils/Theme';
import styles from './ArticleCard.module.css';
import type { ArticleCardInfoLayout } from './ArticleCardInfoLayout';
import type { ArticleCardLayout } from './ArticleCardLayout';
import type { ArticleCardPictureRatio } from './ArticleCardPictureRatio';
import type { ArticleCardTypography } from './ArticleCardTypography';

export interface ArticleCardProps {
    articleUrl: string;
    pictureRatio: ArticleCardPictureRatio;
    title?: HtmlString;
    infoLayout?: ArticleCardInfoLayout;
    image?: BaseImage;
    pictureProfile?: PictureProfile;
    description?: HtmlString;
    layout?: ArticleCardLayout;
    typography?: ArticleCardTypography;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

export const ArticleCard: React.FC<ArticleCardProps> = props => {
    const contextualTheme = useTheme();
    const {
        title,
        articleUrl,
        pictureRatio,
        image,
        description,
        pictureProfile = PictureProfiles.contentCard,
        layout = 'vertical',
        typography = 'large',
        infoLayout = 'limited',
        theme = contextualTheme,
    } = props;
    const { clickableRef, onProxyMouseDown, onProxyMouseUp } = useClickProxyAllowingTextSelection<HTMLAnchorElement>();

    const firstLetter = title?.value.charAt(0).toLowerCase() as IconName;

    const pictureClassNames = classNames(styles.Picture, styles[`Picture--${pictureRatio}`]);

    return (
        <div
            role="none"
            className={classNames(
                styles.ArticleCard,
                styles[`Variant--${layout}`],
                infoLayout === 'full' && styles['Variant--fullInfo'],
                typography === 'small' && styles['Variant--smallTypography']
            )}
            onMouseDown={onProxyMouseDown}
            onMouseUp={onProxyMouseUp}
            tabIndex={-1}
        >
            <div className={styles.PictureContainer}>
                {image ? (
                    <Picture {...image} profile={pictureProfile} theme={theme} className={pictureClassNames} />
                ) : (
                    <PicturePlaceholder theme={theme} className={pictureClassNames} />
                )}
            </div>
            <div className={styles.Info}>
                <h3 className={styles.Heading}>
                    <Link href={articleUrl} type={LinkType.Clear} ref={clickableRef}>
                        {title && <HtmlDiv html={title} />}
                    </Link>
                </h3>
                <div className={styles.AdditionalInfo}>
                    {description && <HtmlDiv html={description} className={styles.Description} />}
                    {firstLetter && <SvgIcon iconName={firstLetter} className={styles.LetterIcon} />}
                </div>
            </div>
        </div>
    );
};
